/**
 * Step-up (docs/53 §4.2): when a write answers step_up_required, this sheet asks for Face ID or a fresh emailed
 * code, then the write retries once. Cancel resolves false and the write reports the refusal.
 */
import { Button, OtpInput, Sheet } from '@ff/app-ui';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { call, errorText } from '../lib/api.ts';
import { setStepUpHandler } from '../lib/data.ts';
import { S } from '@ff/app-ui/copy';
import { getPasskeyAssertion, passkeysSupported } from './webauthn.ts';

let resolver: ((ok: boolean) => void) | null = null;

export function StepUpHost(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [masked, setMasked] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [passkey, setPasskey] = useState<Awaited<ReturnType<typeof call<'authStepUpOptions'>>>['passkey']>(null);

  useEffect(() => {
    setStepUpHandler(
      () =>
        new Promise<boolean>((resolve) => {
          resolver = resolve;
          setMasked(null);
          setCode('');
          setError(null);
          setOpen(true);
          void call('authStepUpOptions').then((o) => setPasskey(o.passkey), () => setPasskey(null));
        }),
    );
  }, []);

  const finish = (ok: boolean) => {
    setOpen(false);
    resolver?.(ok);
    resolver = null;
  };

  const verify = async (c: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await call('authStepUp', { body: { method: 'email_code', code: c } });
      if (r.result === 'stepped_up') finish(true);
    } catch (e) {
      setError(errorText(e, S.errors.offlineSend));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={() => finish(false)} title={S.stepUp.title} closeLabel={S.app.cancel}>
      <div class="stack">
        <p>{S.stepUp.lead}</p>
        {passkey && passkeysSupported() && (
          <Button
            variant="primary"
            block
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const credential = await getPasskeyAssertion(passkey);
                const r = await call('authStepUp', { body: { method: 'passkey', credential } });
                if (r.result === 'stepped_up') finish(true);
              } catch (e) {
                setError(errorText(e, S.errors.offlineSend));
              } finally {
                setBusy(false);
              }
            }}
          >
            {S.stepUp.passkey}
          </Button>
        )}
        {masked === null ? (
          <Button
            variant={passkey ? 'secondary' : 'primary'}
            block
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await call('authStepUp', { body: { method: 'email_code_start' } });
                if (r.result === 'code_sent') setMasked(r.maskedEmail);
                else finish(true);
              } catch (e) {
                setError(errorText(e, S.errors.offlineSend));
              } finally {
                setBusy(false);
              }
            }}
          >
            {S.stepUp.email}
          </Button>
        ) : (
          <>
            <p>{S.stepUp.sent(masked)}</p>
            <OtpInput label={S.signin.codeLabel} digitLabel={S.signin.digit} value={code} onChange={setCode} onComplete={(c) => void verify(c)} error={error} disabled={busy} />
            <Button variant="primary" block busy={busy} disabled={code.length !== 6} onClick={() => void verify(code)}>
              {S.stepUp.confirm}
            </Button>
          </>
        )}
        {error && masked === null && <p class="field-error">{error}</p>}
      </div>
    </Sheet>
  );
}
